import { Tag } from 'antd';
import type { MouseEvent, PointerEvent } from 'react';
import { useState } from 'react';
import { clampPercent, clampUnit, type PlanPoint } from '@/features/inventorization/position/floorplan-marker-geometry';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export function PlainFloorplanPicker({
  existingPins,
  imageUrl,
  onPick,
  selectedMarker,
}: {
  existingPins: PlanPoint[];
  imageUrl: string;
  onPick: (point: PlanPoint) => void;
  selectedMarker: PlanPoint | null;
}) {
  const [isDragging, setIsDragging] = useState(false);

  function pickFromPointer(event: MouseEvent<HTMLButtonElement> | PointerEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect();

    onPick({
      x: clampUnit((event.clientX - rect.left) / rect.width),
      y: clampUnit((event.clientY - rect.top) / rect.height),
    });
  }

  return (
    <button
      aria-label={translateInventoryText(
        'app.inventory.position.floorplan.markerPickerAria',
        'Floorplan marker picker',
      )}
      onClick={pickFromPointer}
      onPointerMove={event => {
        if (isDragging) {
          pickFromPointer(event);
        }
      }}
      onPointerUp={() => setIsDragging(false)}
      style={{
        background: '#fff',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        cursor: 'crosshair',
        display: 'block',
        overflow: 'hidden',
        padding: 0,
        position: 'relative',
        touchAction: 'none',
        width: '100%',
      }}
      type="button"
    >
      <img
        alt={translateInventoryText('app.inventory.position.floorplan.imageAlt', 'Floorplan')}
        src={imageUrl}
        style={{ display: 'block', width: '100%' }}
      />
      {existingPins.map(pin => (
        <PlanPin color="#595959" key={pin.key ?? `${pin.x}:${pin.y}`} point={pin} shape="pin" size={20} />
      ))}
      {selectedMarker ? (
        <PlanPin
          color="#f5222d"
          label={translateInventoryText('app.inventory.position.map.thisMeter', 'This meter')}
          onPointerDown={event => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragging(true);
          }}
          point={selectedMarker}
          size={20}
        />
      ) : null}
    </button>
  );
}

function PlanPin({
  color,
  label,
  onPointerDown,
  point,
  shape = 'circle',
  size,
}: {
  color: string;
  label?: string;
  onPointerDown?: (event: PointerEvent<HTMLDivElement>) => void;
  point: PlanPoint;
  shape?: 'circle' | 'pin';
  size: number;
}) {
  const isPin = shape === 'pin';

  return (
    <div
      onPointerDown={onPointerDown}
      title={label}
      style={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        left: `${clampPercent(point.x * 100)}%`,
        pointerEvents: onPointerDown ? 'auto' : 'none',
        position: 'absolute',
        top: `${clampPercent(point.y * 100)}%`,
        transform: isPin ? 'translate(-50%, -100%)' : 'translate(-50%, -50%)',
      }}
    >
      <span
        style={{
          background: color,
          border: '2px solid #fff',
          borderRadius: isPin ? '50% 50% 50% 0' : 999,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.24)',
          display: 'block',
          height: size,
          transform: isPin ? 'rotate(-45deg)' : undefined,
          width: size,
        }}
      />
      {label ? <Tag color="red">{label}</Tag> : null}
    </div>
  );
}
